import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { CheckCircle, AlertCircle, Info } from "lucide-react";

export function ManualReviewGuidelines() {
  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Review Guidelines</CardTitle>
          <CardDescription>
            Step-by-step instructions for reviewing and validating bill data
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex items-start gap-3 p-4 bg-blue-50 dark:bg-blue-950/20 rounded-lg border border-blue-200 dark:border-blue-800">
              <Info className="h-5 w-5 text-blue-500 mt-0.5" />
              <div>
                <h4 className="font-semibold mb-2">Review Checklist</h4>
                <ol className="list-decimal list-inside space-y-2 text-sm text-muted-foreground">
                  <li>Verify account number matches the bill exactly</li>
                  <li>Check bill date and due date are in correct format (YYYY-MM-DD)</li>
                  <li>Confirm amount due matches the total shown on the bill</li>
                  <li>Validate meter number if visible on the bill</li>
                  <li>Ensure billing period dates are accurate</li>
                  <li>Review consumption values and units (kWh, gallons, therms)</li>
                </ol>
              </div>
            </div>

            <div className="space-y-2">
              <h4 className="font-semibold">Common Issues to Watch For:</h4>
              <ul className="space-y-2">
                <li className="flex items-start gap-2 text-sm">
                  <AlertCircle className="h-4 w-4 text-orange-500 mt-0.5 flex-shrink-0" />
                  <span>Incorrect account number format (check for extra spaces or characters)</span>
                </li>
                <li className="flex items-start gap-2 text-sm">
                  <AlertCircle className="h-4 w-4 text-orange-500 mt-0.5 flex-shrink-0" />
                  <span>Date format inconsistencies (MM/DD/YYYY vs DD/MM/YYYY)</span>
                </li>
                <li className="flex items-start gap-2 text-sm">
                  <AlertCircle className="h-4 w-4 text-orange-500 mt-0.5 flex-shrink-0" />
                  <span>Decimal placement errors (125.50 vs 12550 or 1.2550)</span>
                </li>
                <li className="flex items-start gap-2 text-sm">
                  <AlertCircle className="h-4 w-4 text-orange-500 mt-0.5 flex-shrink-0" />
                  <span>Meter number confused with account number</span>
                </li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Vendor-Specific Notes</CardTitle>
        </CardHeader>
        <CardContent>
          <Accordion type="single" collapsible className="w-full">
            <AccordionItem value="pge">
              <AccordionTrigger>Pacific Gas & Electric (PG&E)</AccordionTrigger>
              <AccordionContent>
                <ul className="list-disc list-inside space-y-1 text-sm text-muted-foreground">
                  <li>Account numbers: 10 digits, format XXXX-XXXX-XX</li>
                  <li>Bill date always on top-right corner</li>
                  <li>Consumption shown in kWh for electric, therms for gas</li>
                  <li>Watch for: Rate changes mid-period (shows as line item)</li>
                </ul>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="edison">
              <AccordionTrigger>Southern California Edison</AccordionTrigger>
              <AccordionContent>
                <ul className="list-disc list-inside space-y-1 text-sm text-muted-foreground">
                  <li>Account numbers: 12 digits</li>
                  <li>Sometimes includes "estimated" consumption - flag these</li>
                  <li>Watch for: Solar credits (negative amounts)</li>
                </ul>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="water">
              <AccordionTrigger>Municipal Water Districts</AccordionTrigger>
              <AccordionContent>
                <ul className="list-disc list-inside space-y-1 text-sm text-muted-foreground">
                  <li>Account numbers vary by district</li>
                  <li>Consumption in gallons, CCF, or HCF</li>
                  <li>Watch for: Sewer charges bundled with water</li>
                </ul>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Keyboard Shortcuts</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Open next bill</span>
              <kbd className="px-2 py-1 bg-muted rounded text-xs font-mono">J</kbd>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Open previous bill</span>
              <kbd className="px-2 py-1 bg-muted rounded text-xs font-mono">K</kbd>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Save and continue</span>
              <kbd className="px-2 py-1 bg-muted rounded text-xs font-mono">Ctrl + S</kbd>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Flag for supervisor</span>
              <kbd className="px-2 py-1 bg-muted rounded text-xs font-mono">F</kbd>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
